import config from '../config/2026.json';

export const getConfig = () => config;

export const calcBaseIPC = (rent) => {
  return rent * config.ipcRate;
};

export const calcVariation = (prev, next) => {
  if (prev === 0) return 0;
  return (next - prev) / prev;
};

export const calcExcess = (variation) => {
  if (variation <= config.ipcRate) return 0;
  return variation - config.ipcRate;
};

export const calcSharedMonthlyFromAnnual = (annual, share) => {
  return (annual * share) / 12;
};

export const calcTaxImpact = (taxPrev, taxNext, share) => {
  const variation = calcVariation(taxPrev, taxNext);
  const excessRate = calcExcess(variation);
  
  if (excessRate === 0) {
    return {
      applies: false,
      variation: variation,
      variationLabel: `${(variation * 100).toFixed(1)} %`,
      amount: 0,
      reason: 'below_ipc'
    };
  }
  
  const annualIncrease = taxNext - taxPrev;
  const annualIpcIncrease = taxPrev * config.ipcRate;
  const annualExcess = Math.max(0, annualIncrease - annualIpcIncrease);
  const userShare = annualExcess * share;
  const monthlyAmount = userShare / 12;
  
  return {
    applies: true,
    variation: variation,
    variationLabel: `${(variation * 100).toFixed(1)} %`,
    annualIncrease: annualIncrease,
    annualIpcIncrease: annualIpcIncrease,
    annualExcess: annualExcess,
    excessRate: excessRate,
    userShareAnnual: userShare,
    amount: monthlyAmount,
    reason: 'above_ipc'
  };
};

export const calcCombinedTaxImpact = (municipalPrev, municipalNext, schoolPrev, schoolNext, share) => {
  const totalPrev = (municipalPrev || 0) + (schoolPrev || 0);
  const totalNext = (municipalNext || 0) + (schoolNext || 0);
  
  const municipalImpact = calcTaxImpact(municipalPrev || 0, municipalNext || 0, share);
  const schoolImpact = calcTaxImpact(schoolPrev || 0, schoolNext || 0, share);
  
  const totalVariation = calcVariation(totalPrev, totalNext);
  const combinedAmount = municipalImpact.amount + schoolImpact.amount;
  const applies = municipalImpact.applies || schoolImpact.applies;
  
  return {
    applies,
    municipal: municipalImpact,
    school: schoolImpact,
    totalVariation,
    totalVariationLabel: `${(totalVariation * 100).toFixed(1)} %`,
    amount: combinedAmount
  };
};

export const calcInsuranceImpact = (insurancePrev, insuranceNext, share) => {
  if (!insurancePrev || !insuranceNext) {
    return {
      applies: false,
      amount: 0,
      reason: 'unknown'
    };
  }
  
  const variation = calcVariation(insurancePrev, insuranceNext);
  
  if (variation <= config.ipcRate) {
    return {
      applies: false,
      variation: variation,
      variationLabel: `${(variation * 100).toFixed(1)} %`,
      amount: 0,
      reason: 'below_ipc'
    };
  }
  
  const excessRate = variation - config.ipcRate;
  const excessAmount = insurancePrev * excessRate;
  const userShare = excessAmount * share;
  const monthlyAmount = userShare / 12;
  
  return {
    applies: true,
    variation: variation,
    variationLabel: `${(variation * 100).toFixed(1)} %`,
    excessRate: excessRate,
    amount: monthlyAmount,
    reason: 'above_ipc'
  };
};

export const calcWorksImpact = (admissibleCost, share) => {
  if (!admissibleCost || admissibleCost <= 0) {
    return {
      applies: false,
      amount: 0
    };
  }
  
  const annualAmount = admissibleCost * config.worksRate;
  const userShareAnnual = annualAmount * share;
  const monthlyAmount = userShareAnnual / 12;
  
  return {
    applies: true,
    annualAmount: annualAmount,
    userShareAnnual: userShareAnnual,
    amount: monthlyAmount
  };
};

export const calcProrata = (userRent, allRents, estimatedUnits) => {
  if (allRents && allRents.length > 0) {
    const total = allRents.reduce((sum, rent) => sum + rent, 0) + userRent;
    const share = userRent / total;
    return {
      method: 'exact',
      share: share,
      shareLabel: `${(share * 100).toFixed(0)} %`,
      totalUnits: allRents.length + 1
    };
  }
  
  if (estimatedUnits && estimatedUnits > 1) {
    const share = 1 / estimatedUnits;
    return {
      method: 'estimated',
      share: share,
      shareLabel: `${(share * 100).toFixed(0)} %`,
      totalUnits: estimatedUnits
    };
  }
  
  return {
    method: 'single',
    share: 1,
    shareLabel: '100 %',
    totalUnits: 1
  };
};

export const needsProrata = (taxImpact, insuranceImpact, worksImpact, totalUnits) => {
  if (totalUnits <= 1) return false;
  
  return (
    (taxImpact && taxImpact.applies) ||
    (insuranceImpact && insuranceImpact.applies) ||
    (worksImpact && worksImpact.applies)
  );
};

export const calcSummary = (inputs) => {
  const {
    currentRent,
    taxPrev,
    taxNext,
    insurancePrev,
    insuranceNext,
    insuranceKnown,
    admissibleWorksCost,
    prorata
  } = inputs;
  
  const share = prorata?.share || 1;
  
  const baseIPC = calcBaseIPC(currentRent);
  const taxImpact = calcTaxImpact(taxPrev || 0, taxNext || 0, share);
  const insuranceImpact = insuranceKnown 
    ? calcInsuranceImpact(insurancePrev, insuranceNext, share)
    : { applies: false, amount: 0, reason: 'unknown' };
  const worksImpact = calcWorksImpact(admissibleWorksCost, share);
  
  const totalMonthlyIncrease = 
    baseIPC + 
    taxImpact.amount + 
    insuranceImpact.amount + 
    worksImpact.amount;
  
  const newRent = currentRent + totalMonthlyIncrease;
  const percentageIncrease = (totalMonthlyIncrease / currentRent) * 100;
  
  return {
    currentRent,
    baseIPC,
    baseIPCLabel: `${baseIPC.toFixed(2)} $`,
    taxImpact,
    insuranceImpact,
    worksImpact,
    prorata,
    totalMonthlyIncrease,
    totalMonthlyIncreaseLabel: `${totalMonthlyIncrease.toFixed(2)} $`,
    newRent,
    newRentLabel: `${newRent.toFixed(2)} $`,
    percentageIncrease,
    percentageIncreaseLabel: `${percentageIncrease.toFixed(1)} %`,
    ipcRate: config.ipcRate,
    ipcLabel: config.ipcLabel,
    year: config.year,
    isTestIpc: config.isTestIpc
  };
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2
  }).format(amount);
};

export const formatPercent = (rate) => {
  return `${(rate * 100).toFixed(1)} %`;
};
